import React, { useEffect, useState } from "react";
import EventService from "service/event/EventService";
import MOBILE from "mobile/mobileConfig";
import { useMobileQuery } from "mobile/useIsMobile";
import { useToastStackHeight } from "mobile/toastStack";
import "./toastHost.scss";

/**
 * Listens for EventService "showError" and renders it.
 *
 * Services across the app report failures by emitting on EventService and keep
 * no local error state — so without a subscriber mounted, every one of those
 * errors is silently dropped and a failed request just looks like empty data.
 * This is the missing half of that contract.
 */
const AUTO_DISMISS_MS = 5000;

const ToastHost = () => {
  const [toasts, setToasts] = useState([]);
  /* MOBILE ONLY: publish this stack's height so the login page's own toast can
     sit below it on a phone instead of on top of it (src/mobile/toastStack.js).
     The side-effect-free query — this renders on /login, which must not get
     the mobile root class. On desktop the hook does nothing. */
  const stackRef = useToastStackHeight(
    useMobileQuery() && MOBILE.loginToastStack,
    toasts.length > 0
  );

  useEffect(() => {
    let nextId = 0;
    const timers = new Set();

    const onShowError = (payload) => {
      const message =
        (payload && payload.message) || "Something went wrong.";
      // tone: "success" renders green — services emit the same event for both
      // outcomes, so the payload carries the flavour.
      const tone = (payload && payload.tone) || "error";
      const id = ++nextId;

      setToasts((prev) => [...prev, { id, message, tone }]);

      const timer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        timers.delete(timer);
      }, AUTO_DISMISS_MS);
      timers.add(timer);
    };

    EventService.addListener("showError", onShowError);

    return () => {
      EventService.removeListener("showError", onShowError);
      timers.forEach(clearTimeout);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-host" role="status" aria-live="polite" ref={stackRef}>
      {toasts.map((t) => (
        <div className={`toast${t.tone === "success" ? " is-success" : ""}`} key={t.id}>
          <span>{t.message}</span>
          <button
            type="button"
            className="toast-close"
            aria-label="Dismiss"
            onClick={() =>
              setToasts((prev) => prev.filter((x) => x.id !== t.id))
            }
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastHost;
